/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { createSlice, PayloadAction, type ActionReducerMapBuilder } from "@reduxjs/toolkit"
import { submissionCommitted } from "features/userParameters/submissionCommitted"
import { InitialScrsTable } from "services/computations/types"
import { assertFiniteColumn } from "services/computations/utils"

import type { RootState } from "app/store"
import { DeepReadonly } from "utils/readonly"

// AIDEV-NOTE: Keep reducer inputs deeply readonly and return new state objects for strict immutability linting.

type ReadonlyInitialScrsTable = DeepReadonly<InitialScrsTable>

// The slider bounds derived from a table; null where the column they come from is empty.
export interface InitialScrsRanges {
  years: {
    min: number
    max: number
  } | null
  absoluteDrawdowns: {
    min: number
    max: number
  } | null
  capeRanges: {
    ern: {
      min: number
      max: number
    } | null
    shiller: {
      min: number
      max: number
    } | null
  }
}

interface InitialScrsState {
  data: InitialScrsTable | null
  fresh: boolean | null
  computedRanges: InitialScrsRanges
  dataReady: boolean
}

const initialState: InitialScrsState = {
  data: null,
  fresh: null,
  computedRanges: {
    years: null,
    absoluteDrawdowns: null,
    capeRanges: {
      ern: null,
      shiller: null,
    },
  },
  dataReady: false,
}

// Helper functions to compute ranges
// AIDEV-NOTE: issues #781 and #1061. Each helper below derives a slider bound by
// spreading a column into Math.min/Math.max, or by mapping 1/caey over one, and both
// coerce a non-finite entry instead of rejecting it: a null drawdown yields a min of -0,
// shown to the user as "no drawdown" for a dataset whose real worst drawdown is 30%, and
// a null CAEY yields 1/null === Infinity, which then wins Math.max and becomes the CAPE
// slider's upper bound. Guarding at the entry of each helper, naming the column it reads,
// keeps this surface consistent with the computation-layer consumers of the same columns
// (filterCohorts, calculateFinalValueProportions).
//
// Throwing rather than returning the `null` these helpers already use for "no data" is
// deliberate: `null` would leave dataReady false with nothing reported, which is the
// silent degradation the guard exists to prevent. The throw is meant to be met first in
// submitForm (features/userParameters/UserParametersForm.tsx), which calls
// computeInitialScrsRanges below before it stores anything, inside the try/catch that
// reports to Sentry and raises the calculation-error toast - so a rejected table surfaces
// exactly as a computation-layer failure does and leaves the store on the previous
// submission (#1125). The reducers run the same scan again on the table they store; that
// one only throws for a caller that skipped the first.
const computeYearRange = (data: ReadonlyInitialScrsTable): { min: number; max: number } | null => {
  assertFiniteColumn(data.year, "year", "computeYearRange")
  if (data.year.length === 0) {
    return null
  }
  return {
    min: Math.min(...data.year),
    max: Math.max(...data.year),
  }
}

const computeDrawdownRange = (
  data: ReadonlyInitialScrsTable
): { min: number; max: number } | null => {
  assertFiniteColumn(
    data.real_equity_drawdown_1M_lag,
    "real_equity_drawdown_1M_lag",
    "computeDrawdownRange"
  )
  if (data.real_equity_drawdown_1M_lag.length === 0) {
    return null
  }
  return {
    min: -Math.max(...data.real_equity_drawdown_1M_lag),
    max: -Math.min(...data.real_equity_drawdown_1M_lag),
  }
}

// Add helper functions to compute CAPE ranges
const computeErnCapeRange = (
  data: ReadonlyInitialScrsTable
): { min: number; max: number } | null => {
  assertFiniteColumn(data.caey_ern_1M_lag, "caey_ern_1M_lag", "computeErnCapeRange")
  if (data.caey_ern_1M_lag.length === 0) {
    return null
  }
  // CAEY values are 1/CAPE, so we need to convert back to CAPE
  const capeValues = data.caey_ern_1M_lag.map(caey => 1 / caey)
  return {
    min: Math.min(...capeValues),
    max: Math.max(...capeValues),
  }
}

const computeShillerCapeRange = (
  data: ReadonlyInitialScrsTable
): { min: number; max: number } | null => {
  assertFiniteColumn(data.caey_shiller_1M_lag, "caey_shiller_1M_lag", "computeShillerCapeRange")
  if (data.caey_shiller_1M_lag.length === 0) {
    return null
  }
  // CAEY values are 1/CAPE, so we need to convert back to CAPE
  const capeValues = data.caey_shiller_1M_lag.map(caey => 1 / caey)
  return {
    min: Math.min(...capeValues),
    max: Math.max(...capeValues),
  }
}

// Throws NonFiniteColumnError, from the helper that reads it, for a non-finite entry in
// any column a bound is derived from. The helpers run in a fixed order - year, drawdown,
// ERN CAEY, Shiller CAEY - so a table corrupt in several of those columns names the first.
export const computeInitialScrsRanges = (data: ReadonlyInitialScrsTable): InitialScrsRanges => ({
  years: computeYearRange(data),
  absoluteDrawdowns: computeDrawdownRange(data),
  capeRanges: {
    ern: computeErnCapeRange(data),
    shiller: computeShillerCapeRange(data),
  },
})

// The state for a table just fetched for the current submission. Shared by setInitialScrs
// and submissionCommitted, which must store a table identically.
const storeFetchedTable = (
  state: DeepReadonly<InitialScrsState>,
  data: ReadonlyInitialScrsTable
): InitialScrsState => {
  const computedRanges = computeInitialScrsRanges(data)
  const { years, absoluteDrawdowns, capeRanges } = computedRanges

  return {
    ...state,
    data,
    // AIDEV-NOTE: #465 - this data was just fetched for the current submission, so it
    // is fresh by construction. Previously left `fresh` untouched, which let
    // setInitialScrsFresh(false) followed by setInitialScrs(data) produce
    // fresh:false, dataReady:true - violating the invariant setInitialScrsFresh's own
    // logic below assumes (fresh:false implies dataReady:false).
    fresh: true,
    computedRanges,
    dataReady: !!(years && absoluteDrawdowns && capeRanges.ern && capeRanges.shiller),
  }
}

const initialScrsSlice = createSlice({
  name: "initialScrs",
  initialState,
  reducers: {
    setInitialScrs: (
      state: DeepReadonly<InitialScrsState>,
      action: DeepReadonly<PayloadAction<ReadonlyInitialScrsTable>>
    ): InitialScrsState => storeFetchedTable(state, action.payload),
    setInitialScrsFresh: (
      state: DeepReadonly<InitialScrsState>,
      action: DeepReadonly<PayloadAction<boolean>>
    ): InitialScrsState => ({
      ...(state as InitialScrsState),
      fresh: action.payload,
      // Reset dataReady when data is no longer fresh
      dataReady: action.payload ? state.dataReady : false,
    }),
  },
  extraReducers: (builder: Readonly<ActionReducerMapBuilder<InitialScrsState>>) => {
    builder.addCase(
      submissionCommitted,
      (
        state: DeepReadonly<InitialScrsState>,
        action: DeepReadonly<ReturnType<typeof submissionCommitted>>
      ): InitialScrsState => storeFetchedTable(state, action.payload.initialScrsTable)
    )
  },
})

export const { setInitialScrs, setInitialScrsFresh } = initialScrsSlice.actions

// Selectors
export const selectInitialScrs = (state: DeepReadonly<RootState>) => state.initialScrs
export const selectComputedRanges = (state: DeepReadonly<RootState>) =>
  state.initialScrs.computedRanges
export const selectInitialScrsDataReady = (state: DeepReadonly<RootState>) =>
  state.initialScrs.dataReady

export default initialScrsSlice.reducer
